import { useNavigate, useParams } from 'react-router-dom';
import {
  MAX_PATIENT_MESSAGES,
  useGetConversation,
} from '../hooks/useGetConversation';
import { ReactComponent as Messages } from 'assets/icons/messagesQuestion.svg';
import { ReactComponent as Close } from 'assets/icons/close.svg';
import SendForm from './SendForm';
import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useIsMobile } from 'hooks/useIsMobile';
import {
  InMailMessageOutput,
  InMailParticipantOutput,
} from 'graphql/generated/hasura';
import Message from '../../providers-view-selection/components/bio/messages/Message';
import { useAuth } from 'auth/context/AuthContext';
import { STANDARD_DATE, TIME_FORMAT } from 'utilities/dateHelper';
import moment from 'moment';
import { twJoin, twMerge } from 'tailwind-merge';
import Loader from 'components/loaderComponent';
import ConversationHeader from './ConversationHeader';
import { useInfiniteScroll } from 'hooks/useInfiniteScroll';
import Alert from '../../../components/alertComponent';
import { Roles } from '../../../firebase/interfaces';
import { useSupportChat } from './SupportContext';

interface GroupByDayMessages {
  day: string;
  messages: InMailMessageOutput[];
  patient?: InMailParticipantOutput;
  provider?: InMailParticipantOutput;
  userClassName?: string;
  textClassName?: string;
}

export const GroupByDayMessages: React.FC<GroupByDayMessages> = ({
  day,
  patient,
  provider,
  messages,
  userClassName = 'bg-clc-blue text-white',
  textClassName = 'bg-base-content',
}) => {
  const { user, selectedAccount } = useAuth();

  return (
    <div className="flex flex-col gap-2.5">
      <p className="flex items-center before:flex-grow before:border-b before:border-gray-opacity-15 before:content-[''] after:flex-grow after:border-b after:border-gray-opacity-15 after:content-['']">
        <span className="desktop:bg-base-content bg-white rounded-full px-4 py-1 text-xs text-med-gray-3">
          {moment(day).format(STANDARD_DATE)}
        </span>
      </p>
      {[...messages]
        .sort((a, b) => moment(a.date).diff(b.date))
        .map(({ attachments, text, date, senderCodexId }) => (
          <Message
            key={date}
            sender={senderCodexId === patient?.codexId ? patient : provider}
            attachments={attachments}
            date={date}
            senderCodexId={senderCodexId}
            isProvider={
              selectedAccount !== senderCodexId && user?.uuid !== senderCodexId
            }
            text={text}
            dateFormat={TIME_FORMAT}
            userClassName={userClassName}
            textClassName={textClassName}
          />
        ))}
    </div>
  );
};

const InMailConversation = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const { chatId: supportChatId, chat } = useSupportChat();

  const {
    loading,
    conversation,
    messages,
    onSend,
    disabledSend,
    messagesCount,
    isMaxMessages,
    handleLoadMore,
    isFetchingMore,
  } = useGetConversation(conversationId as string);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<{
    scrollTop: number;
    scrollHeight: number;
  } | null>(null);

  const shouldRestoreScrollRef = useRef(false);
  const handleLoadMoreWithScroll = useCallback(() => {
    const container = messagesContainerRef.current;

    if (!container || isFetchingMore) {
      return;
    }

    scrollPositionRef.current = {
      scrollTop: container.scrollTop,
      scrollHeight: container.scrollHeight,
    };

    shouldRestoreScrollRef.current = true;

    handleLoadMore();
  }, [handleLoadMore, isFetchingMore]);

  useLayoutEffect(() => {
    if (!shouldRestoreScrollRef.current) {
      return;
    }

    const container = messagesContainerRef.current;
    const previousPosition = scrollPositionRef.current;

    if (!container || !previousPosition) {
      return;
    }

    container.scrollTop = previousPosition.scrollTop;

    shouldRestoreScrollRef.current = false;
    scrollPositionRef.current = null;
  }, [messages]);

  const loaderRef = useInfiniteScroll(handleLoadMoreWithScroll, {
    root: messagesContainerRef.current,
  });

  const handleBodyClass = useCallback(() => {
    if (isMobile) {
      document.body.classList.add('!overflow-hidden');
    } else {
      document.body.classList.remove('!overflow-hidden');
    }
  }, [isMobile]);

  useEffect(() => {
    handleBodyClass();
    window.addEventListener('resize', handleBodyClass);
    return () => {
      window.removeEventListener('resize', handleBodyClass);
      document.body.classList.remove('!overflow-hidden');
    };
  }, [handleBodyClass]);

  return (
    <>
      <section className="max-desktop:hidden flex-1 flex flex-col">
        <div className="flex-1 relative flex flex-col gap-2.5 bg-white rounded-10">
          <div className="flex items-center p-30 pb-5 border-b border-gray-opacity-15">
            <ConversationHeader conversation={conversation} />
          </div>
          <div
            className={twMerge(
              'absolute left-1/2 -translate-x-1/2 top-28 w-max hidden',
              user?.role === Roles.PATIENT && 'block',
            )}
          >
            <Alert
              type="info-blue"
              textClassName="text-med-gray-3 text-sm -my-1 w-full font-medium"
              text={`You have ${messagesCount}/${MAX_PATIENT_MESSAGES} messages available. The limit will be refreshed after next appointment`}
            />
          </div>
          <div
            ref={messagesContainerRef}
            className={twJoin(
              'h-full p-30 pt-0 mt-auto flex flex-col-reverse gap-2.5 max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-thumb-rounded-full scrollbar-track-rounded-full',
              (loading || disabledSend) && 'opacity-50',
            )}
          >
            <div
              className={twMerge(
                'flex flex-col items-center gap-2.5 my-auto',
                messages.length && 'hidden',
              )}
            >
              <Messages className="w-9 h-9 max-w-[2.25rem] fill-med-gray-3" />
              <p className="text-center text-med-gray-3 font-medium">
                {'No messages yet'}
              </p>
            </div>
            {messages.map(([day, messages]) => (
              <GroupByDayMessages
                provider={conversation?.provider}
                patient={conversation?.patient}
                messages={messages}
                day={day}
                key={day}
              />
            ))}
            {messages.length > 0 && (
              <div ref={loaderRef} className="h-4 w-full shrink-0" />
            )}
            <div
              className={twJoin(
                'hidden z-50 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
                (loading || disabledSend) && '!block',
              )}
            >
              <Loader />
            </div>
          </div>
          <SendForm
            isMaxMessages={isMaxMessages}
            handleSend={onSend}
            disabled={disabledSend}
            supportChatId={supportChatId}
            chat={chat}
          />
        </div>
      </section>

      <section className="desktop:hidden flex bg-white z-50 fixed top-20 left-0 w-screen h-[calc(100vh_-_5rem)]">
        <div className="flex h-full w-full flex-col justify-between">
          <div className="flex bg-white border-b border-gray-opacity-15 items-center gap-2.5 px-7 py-5">
            <ConversationHeader conversation={conversation} />
            <Close
              className="w-5 h-5 min-w-[20px] cursor-pointer ml-auto mr-4"
              onClick={() => navigate(-1)}
            />
          </div>
          <div
            className={twJoin(
              'h-full p-30 pt-0 flex flex-col-reverse gap-2.5 max-h-[670px] overflow-y-auto scrollbar-thin scrollbar-thumb-rounded-full scrollbar-track-rounded-full',
              (loading || disabledSend) && 'opacity-50',
            )}
          >
            <div
              className={twMerge(
                'flex flex-col items-center gap-2.5 my-auto',
                messages.length && 'hidden',
              )}
            >
              <Messages className="w-9 h-9 max-w-[2.25rem] fill-med-gray-3" />
              <p className="text-center text-med-gray-3 font-medium">
                {'No messages yet'}
              </p>
            </div>
            {messages.map(([day, messages]) => (
              <GroupByDayMessages
                provider={conversation?.provider}
                patient={conversation?.patient}
                messages={messages}
                day={day}
                key={day}
              />
            ))}
          </div>
          <SendForm
            isMaxMessages={isMaxMessages}
            handleSend={onSend}
            disabled={disabledSend}
            supportChatId={supportChatId}
            chat={chat}
          />
        </div>
      </section>
    </>
  );
};

export default InMailConversation;
